import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  Package, DollarSign, Hash
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const HSNSummaryPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hsnData, setHsnData] = useState([]);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    hsn: '',
    description: '',
    value: { min: '', max: '' }
  });

  useEffect(() => {
    if (businessId) {
      fetchHSNData();
    }
  }, [month, year, businessId]);

  const fetchHSNData = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.gstr1({ month, year });
      setHsnData(response.data?.hsn_summary || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'hsn-summary', format, month, year });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HSN_Summary_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  // Apply column filters
  const filteredData = hsnData.filter(item => {
    const matchesHsn = !columnFilters.hsn || 
      item.hsn?.toLowerCase().includes(columnFilters.hsn.toLowerCase());
    const matchesDesc = !columnFilters.description || 
      item.description?.toLowerCase().includes(columnFilters.description.toLowerCase());
    const matchesValue = (!columnFilters.value?.min || (item.taxable || 0) >= parseFloat(columnFilters.value.min)) &&
      (!columnFilters.value?.max || (item.taxable || 0) <= parseFloat(columnFilters.value.max));
    return matchesHsn && matchesDesc && matchesValue;
  });

  const totals = filteredData.reduce((acc, item) => {
    acc.qty += item.qty || 0;
    acc.value += item.value || 0;
    acc.taxable += item.taxable || 0;
    acc.cgst += item.cgst || 0;
    acc.sgst += item.sgst || 0;
    acc.igst += item.igst || 0;
    return acc;
  }, { qty: 0, value: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0 });

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  return (
    <div className="h-full flex flex-col" data-testid="hsn-summary-page">
      {/* Premium Header */}
      <ListPageHeader
        title="HSN Summary"
        stats={[
          { icon: Hash, label: 'HSN Codes', value: filteredData.length, color: 'slate' },
          { icon: Package, label: 'Total Qty', value: totals.qty.toLocaleString(), color: 'blue' },
          { icon: DollarSign, label: 'Taxable Value', value: formatCompactCurrency(totals.taxable), color: 'emerald' },
          { icon: DollarSign, label: 'Total GST', value: formatCompactCurrency(totals.cgst + totals.sgst + totals.igst), color: 'purple' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading HSN summary...</p>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No HSN data for this period</p>
              <p className="text-[13px] text-slate-400 mt-1">HSN-wise summary will appear here when you have sales data</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          HSN Code
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.hsn}
                            onChange={(val) => setColumnFilters({...columnFilters, hsn: val})}
                            placeholder="Filter HSN..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '22%' }}>
                        <div className="flex items-center gap-1">
                          Description
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.description}
                            onChange={(val) => setColumnFilters({...columnFilters, description: val})}
                            placeholder="Filter desc..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-center" style={{ width: '8%' }}>UQC</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>Qty</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Total Value</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Taxable
                          <ColumnFilter 
                            type="number" 
                            value={columnFilters.value}
                            onChange={(val) => setColumnFilters({...columnFilters, value: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-center" style={{ width: '6%' }}>Rate</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '8%' }}>CGST</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '8%' }}>SGST</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '8%' }}>IGST</th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredData.map((item, idx) => (
                      <tr 
                        key={idx} 
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                      >
                        <td className="tl-td" style={{ width: '10%' }}>
                          <span className="inline-flex px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px] font-mono font-medium">
                            {item.hsn}
                          </span>
                        </td>
                        <td className="tl-td tl-cell-primary" style={{ width: '22%' }}>{item.description}</td>
                        <td className="tl-td text-center tl-cell-regular" style={{ width: '8%' }}>{item.uqc || 'NOS'}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '10%' }}>{(item.qty || 0).toLocaleString()}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '12%' }}>₹{(item.value || 0).toLocaleString()}</td>
                        <td className="tl-td text-right text-[13px] font-semibold text-slate-800" style={{ width: '12%' }}>₹{(item.taxable || 0).toLocaleString()}</td>
                        <td className="tl-td text-center" style={{ width: '6%' }}>
                          <span className="inline-flex px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium">
                            {item.rate || 0}%
                          </span>
                        </td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '8%' }}>₹{(item.cgst || 0).toLocaleString()}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '8%' }}>₹{(item.sgst || 0).toLocaleString()}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '8%' }}>₹{(item.igst || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-teal-50 border-t-2 border-teal-200 sticky bottom-0">
                      <td colSpan="3" className="tl-td text-[13px] font-bold text-slate-900">TOTAL</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">{totals.qty.toLocaleString()}</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.value.toLocaleString()}</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.taxable.toLocaleString()}</td>
                      <td className="tl-td"></td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.cgst.toLocaleString()}</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.sgst.toLocaleString()}</td>
                      <td className="tl-td text-right text-[13px] font-bold text-teal-700">₹{totals.igst.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredData.length}</span> HSN codes
                </p>
                <p>
                  Total GST: <span className="font-medium text-slate-700">₹{(totals.cgst + totals.sgst + totals.igst).toLocaleString()}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HSNSummaryPage;
